/**
 * Listen for the Google OAuth popup's completion message via THREE channels,
 * since the app now runs inside an iframe (Shell.js) and Chrome's COOP
 * severs `window.opener` for the popup after the cross-origin round-trip
 * through Google. The popup fans out to all three; the listener subscribes
 * to all three. First wins; the rest are deduped via a shared marker.
 *
 * Usage:
 *   const stop = listenForGoogleConnect(({ code, inboxId, connectedInbox }) => {
 *     // handle the result
 *   });
 *   // later: stop();
 */

const STORAGE_KEY = "google_connect_inbox_result";
const CHANNEL_NAME = "google_connect_inbox";

export const listenForGoogleConnect = (callback) => {
  if (typeof callback !== "function") return () => {};

  let lastTs = 0;
  const handle = (msg) => {
    if (!msg || msg.type !== "GOOGLE_CONNECT_INBOX") return;
    // Dedup: each broadcast carries the same _ts (or no _ts for postMessage path).
    // For postMessage path, fall through. For storage/BC, use _ts to ignore replays.
    if (msg._ts && msg._ts === lastTs) return;
    if (msg._ts) lastTs = msg._ts;
    callback(msg);
  };

  // 1. Classic window.opener postMessage (works when COOP isn't strict).
  const onMessage = (ev) => handle(ev?.data);
  window.addEventListener("message", onMessage);

  // 2. Same-origin BroadcastChannel (works iframe ↔ popup without opener).
  let bc;
  try {
    bc = new BroadcastChannel(CHANNEL_NAME);
    bc.onmessage = (ev) => handle(ev?.data);
  } catch (e) {}

  // 3. localStorage `storage` events (last-resort cross-tab/iframe signal).
  const onStorage = (ev) => {
    if (ev.key !== STORAGE_KEY || !ev.newValue) return;
    try { handle(JSON.parse(ev.newValue)); } catch (e) {}
  };
  window.addEventListener("storage", onStorage);

  // Also check if the popup raced ahead of our listener and already wrote
  // a result before we subscribed. Read once on attach.
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Only consume if it's recent (within 30s) — older values are stale.
      if (parsed?._ts && Date.now() - parsed._ts < 30000) {
        handle(parsed);
      }
    }
  } catch (e) {}

  return () => {
    window.removeEventListener("message", onMessage);
    window.removeEventListener("storage", onStorage);
    try { if (bc) bc.close(); } catch (e) { /* noop */ }
  };
};

/** Clear the storage signal — call after consuming a result so it doesn't fire again. */
export const clearGoogleConnectSignal = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
};

export default listenForGoogleConnect;
